import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ThoughtEntry {
  id: string;
  user_id: string;
  thought_content: string;
  situation: string | null;
  emotion: string | null;
  emotion_intensity: number | null;
  cognitive_distortions: string[];
  alternative_thought: string | null;
  reframed_emotion: string | null;
  reframed_intensity: number | null;
  ai_challenge_used: boolean;
  ai_response: string | null;
  created_at: string;
  updated_at: string;
}

export interface DistortionCount {
  distortionId: string;
  count: number;
}

export function useThoughtEntries(days: number = 30) {
  const [entries, setEntries] = useState<ThoughtEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchEntries = async () => {
    setIsLoading(true);
    setError(null);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError(new Error('Please sign in to view thought entries'));
      setIsLoading(false);
      return;
    }

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error: fetchError } = await supabase
      .from('thought_entries')
      .select('*')
      .eq('user_id', user.id)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });

    if (fetchError) {
      console.error('Error fetching thought entries:', fetchError);
      setError(new Error('Failed to load thought entries'));
    } else {
      setEntries((data as ThoughtEntry[]) || []);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchEntries();
  }, [days]);

  const saveEntry = async (entryData: {
    thought_content: string;
    situation?: string;
    emotion?: string;
    emotion_intensity?: number;
    cognitive_distortions?: string[];
    alternative_thought?: string;
  }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error: insertError } = await supabase
      .from('thought_entries')
      .insert({
        user_id: user.id,
        thought_content: entryData.thought_content,
        situation: entryData.situation || null,
        emotion: entryData.emotion || null,
        emotion_intensity: entryData.emotion_intensity || null,
        cognitive_distortions: entryData.cognitive_distortions || [],
        alternative_thought: entryData.alternative_thought || null
      })
      .select()
      .single();

    if (insertError) throw insertError;
    
    // Refresh entries
    await fetchEntries();
    return data;
  };

  const updateEntry = async (id: string, updates: Partial<ThoughtEntry>) => {
    const { error: updateError } = await supabase
      .from('thought_entries')
      .update(updates)
      .eq('id', id);

    if (updateError) throw updateError;
    await fetchEntries();
  };

  const deleteEntry = async (id: string) => {
    const { error: deleteError } = await supabase
      .from('thought_entries')
      .delete()
      .eq('id', id);

    if (deleteError) throw deleteError;
    await fetchEntries();
  };

  // Calculate distortion counts for pie chart
  const distortionCounts: DistortionCount[] = useMemo(() => {
    const counts: Record<string, number> = {};
    
    entries.forEach(entry => {
      (entry.cognitive_distortions || []).forEach(distortion => {
        counts[distortion] = (counts[distortion] || 0) + 1;
      });
    });

    return Object.entries(counts).map(([distortionId, count]) => ({
      distortionId,
      count
    }));
  }, [entries]);

  const totalThoughts = entries.length;

  return {
    entries,
    isLoading,
    error,
    saveEntry,
    updateEntry,
    deleteEntry,
    distortionCounts,
    totalThoughts,
    refetch: fetchEntries
  };
}
